import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// For Emergent deployments, frontend and backend share the same domain
// Always use window.location.origin for maximum compatibility
const API_URL = window.location.origin;

const AuthContext = createContext(null);

// Create axios instance with auth header
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      // Skip on auth callback page
      if (window.location.pathname === '/auth/callback') {
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('session_token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token and get user data
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data);
      } catch (error) {
        console.log('Auth check failed:', error.message);
        if (error.code === 'ERR_NETWORK') {
          console.log('Network error during auth check');
        }
        localStorage.removeItem('session_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    localStorage.removeItem('session_token');
    setUser(null);
    window.location.href = '/';
  };

  // Check if authenticated (has token)
  const isAuthenticated = !!localStorage.getItem('session_token');

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      login,
      logout,
      setUser,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { api };
